import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react'
import { AuthState, User, LoginRequest } from '../types/api'
import { authService } from '../services/api'
import toast from 'react-hot-toast'
import { setTokens, clearTokens, cleanupOldStorage } from '../utils/tokenStorage'

// Starea inițială
const initialState: AuthState & { rememberMe: boolean } = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
  rememberMe: false, // ✨ REMEMBER ME: stocat pentru inactivity timeout
}

// Tipurile de acțiuni
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; accessToken: string; refreshToken: string; rememberMe?: boolean } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'SET_USER'; payload: User }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_REMEMBER_ME'; payload: boolean }

// Reducer pentru gestionarea stării
const authReducer = (state: AuthState & { rememberMe: boolean }, action: AuthAction): AuthState & { rememberMe: boolean } => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
      }
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        accessToken: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
        isAuthenticated: true,
        isLoading: false,
        rememberMe: action.payload.rememberMe ?? false, // ✨ REMEMBER ME
      }
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        rememberMe: false,
      }
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        rememberMe: false,
      }
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
      }
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      }
    case 'SET_REMEMBER_ME':
      return {
        ...state,
        rememberMe: action.payload,
      }
    default:
      return state
  }
}

// Contextul de autentificare
interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<any>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  completeMFALogin: () => Promise<void>
  rememberMe: boolean  // ✨ REMEMBER ME: expus pentru useSession
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Provider pentru context
interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Funcția de login
  const login = async (credentials: LoginRequest) => {
    try {
      dispatch({ type: 'LOGIN_START' })
      
      const response = await authService.login(credentials)
      
      // Verifică dacă MFA este necesar
      if (response.mfa_required) {
        // Returnează răspunsul pentru a fi gestionat de LoginPage
        dispatch({ type: 'SET_LOADING', payload: false })
        return response
      }
      
      // COOKIE-BASED AUTH: Token-urile sunt setate automat ca httpOnly cookies de backend
      // NU mai salvăm în RAM - cookie-ul se trimite automat în toate requests
      // Pentru backwards compatibility, dacă primim tokens, îi salvăm în RAM (optional)
      if (response.access_token && response.refresh_token) {
        setTokens(response.access_token, response.refresh_token)
      }
      
      // Obține datele complete ale utilizatorului (request va avea cookie automat)
      const user = await authService.getCurrentUser()
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user,
          accessToken: response.access_token || '',  // Optional - cookie-based auth
          refreshToken: response.refresh_token || '', // Optional - cookie-based auth
        },
      })
      
      toast.success('Autentificare reușită!')
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Eroare la autentificare'
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage })
      toast.error(errorMessage)
      throw error
    }
  }

  // Funcția de logout
  const logout = async () => {
    try {
      // Backend-ul va șterge cookie-urile automat (ris_session, refresh_token)
      await authService.logout()
    } catch (error) {
      console.error('Eroare la logout:', error)
    } finally {
      // Șterge token-urile din memoria RAM (dacă există)
      clearTokens()
      dispatch({ type: 'LOGOUT' })
      toast.success('Deconectare reușită!')
    }
  }

  // Funcția pentru refresh-ul datelor utilizatorului
  const refreshUser = async () => {
    try {
      const user = await authService.getCurrentUser()
      dispatch({ type: 'SET_USER', payload: user })
    } catch (error) {
      console.error('Eroare la obținerea datelor utilizatorului:', error)
      // Dacă nu putem obține datele utilizatorului, probabil token-ul a expirat
      dispatch({ type: 'LOGOUT' })
    }
  }

  // Funcția pentru finalizarea login-ului după MFA challenge
  const completeMFALogin = async () => {
    try {
      // După MFA success, cookie-urile sunt deja setate de backend
      // Trebuie doar să obținem user data și să actualizăm starea
      const user = await authService.getCurrentUser()
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user,
          accessToken: 'cookie-managed',
          refreshToken: 'cookie-managed',
        },
      })
      
      toast.success('Autentificare MFA reușită!')
    } catch (error: any) {
      console.error('Eroare la finalizarea login-ului MFA:', error)
      dispatch({ type: 'LOGOUT' })
      toast.error('Eroare la finalizarea autentificării')
      throw error
    }
  }

  // Verifică autentificarea la încărcarea aplicației
  useEffect(() => {
    const checkAuth = async () => {
      // CLEANUP: Elimină token-uri vechi din localStorage dacă există
      cleanupOldStorage()
      
      // SILENT REFRESH: Încearcă să reîncărci sesiunea folosind httpOnly cookie
      try {
        // Call silent-refresh endpoint care verifică httpOnly cookie
        const response = await authService.silentRefresh()
        
        if (response.access_token) {
          // IMPORTANT: Salvăm access_token în memoria RAM ÎNAINTE de a apela getCurrentUser()
          // Altfel Axios interceptor nu găsește token-ul când face request-ul
          setTokens(response.access_token, 'cookie-managed')
          
          // Acum putem obține user data cu token-ul salvat
          const user = await authService.getCurrentUser()
          
          // ✨ REMEMBER ME: Citim din răspunsul silent-refresh
          const rememberMe = response.remember_me ?? false
          
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user,
              accessToken: response.access_token,
              refreshToken: 'cookie-managed', // Managed by backend via httpOnly cookie
              rememberMe, // ✨ REMEMBER ME
            },
          })
          
          console.log(`✅ Silent refresh successful - session restored (remember_me=${rememberMe})`)
          return
        }
      } catch (error) {
        // Silent refresh failed - cookie invalid sau expirat
        console.log('ℹ️ Silent refresh failed - user needs to login')
      }
      
      // Dacă ajungem aici, user trebuie să se logheze manual
      dispatch({ type: 'SET_LOADING', payload: false })
    }

    checkAuth()
  }, [])

  // ============================================================================
  // PROACTIVE TOKEN REFRESH (Enterprise Security Pattern)
  // ============================================================================
  // Access token-ul expiră la 30 minute. Pentru a evita logout-uri neașteptate,
  // facem refresh PROACTIV la fiecare 25 minute (5 minute înainte de expirare).
  // 
  // Beneficii:
  // - Utilizatorul nu mai este deconectat brusc
  // - Token-ul e mereu "fresh" pentru orice operație
  // - Sliding window: sesiunea se prelungește automat
  // ============================================================================
  useEffect(() => {
    if (!state.isAuthenticated) return

    const PROACTIVE_REFRESH_INTERVAL = 25 * 60 * 1000 // 25 minute (5 min înainte de expirare)
    
    const proactiveRefresh = async () => {
      try {
        console.log('🔄 Proactive token refresh...')
        const response = await authService.silentRefresh()
        
        if (response.access_token) {
          setTokens(response.access_token, 'cookie-managed')
          console.log('✅ Proactive refresh successful')
        }
      } catch (error) {
        console.warn('⚠️ Proactive refresh failed:', error)
        // Nu facem logout aici - interceptorul va gestiona 401 când apare
      }
    }

    // Setează interval pentru refresh proactiv
    const intervalId = setInterval(proactiveRefresh, PROACTIVE_REFRESH_INTERVAL)
    
    // Cleanup la unmount sau când user se deloghează
    return () => clearInterval(intervalId)
  }, [state.isAuthenticated])

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    refreshUser,
    completeMFALogin,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Hook pentru utilizarea contextului
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth trebuie folosit în cadrul unui AuthProvider')
  }
  return context
}
